import axios from "axios";
import { baseUrl } from "../config/constants";
import { handleCookies } from "../utils/cookies";

const getToken = () => {
  const { token } = handleCookies.fetchCookies();
  return "Bearer " + token;
};

// Policy API Functions
const fetchPolicies = async (filters = {}) => {
  try {
    const { offset = 0, limit = 10, search = "", law_firm_id = "" } = filters;
    const response = await fetch(`${baseUrl}/policies`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      body: JSON.stringify({
        offset,
        limit,
        search,
        law_firm_id,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch policies");
    }
    return data.data;
  } catch (error) {
    console.error("Error fetching policies:", error);
    throw error;
  }
};

const fetchPolicyDetails = async (id) => {
  try {
    const response = await fetch(`${baseUrl}/policyDetails`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      body: JSON.stringify({ id }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch policy details");
    }
    return data.data;
  } catch (error) {
    console.error("Error fetching policy details:", error);
    throw error;
  }
};

const createPolicy = async (policyData) => {
  try {
    const response = await fetch(`${baseUrl}/createPolicy`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      body: JSON.stringify(policyData),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to create policy");
    }
    return data.data;
  } catch (error) {
    console.error("Error creating policy:", error);
    throw error;
  }
};

const updatePolicy = async (id, policyData) => {
  try {
    const response = await fetch(`${baseUrl}/updatePolicy`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      body: JSON.stringify({ id, ...policyData }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to update policy");
    }
    return data.data;
  } catch (error) {
    console.error("Error updating policy:", error);
    throw error;
  }
};

const deletePolicy = async (id) => {
  try {
    const response = await fetch(`${baseUrl}/deletePolicy`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      body: JSON.stringify({ id }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to delete policy");
    }
    return data.data;
  } catch (error) {
    console.error("Error deleting policy:", error);
    throw error;
  }
};

// Address API Functions
const fetchAddresses = async (policyId) => {
  try {
    const response = await fetch(`${baseUrl}/policyAddresses`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      body: JSON.stringify({ policy_id: policyId }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch addresses");
    }
    return data.data;
  } catch (error) {
    console.error("Error fetching addresses:", error);
    throw error;
  }
};

const createAddress = async (policyId, addressData) => {
  try {
    const response = await fetch(`${baseUrl}/createPolicyAddress`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      body: JSON.stringify({ policy_id: policyId, ...addressData }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to create address");
    }
    return data.data;
  } catch (error) {
    console.error("Error creating address:", error);
    throw error;
  }
};

const updateAddress = async (policyId, addressId, addressData) => {
  try {
    const response = await fetch(`${baseUrl}/updatePolicyAddress`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      body: JSON.stringify({
        policy_id: policyId,
        address_id: addressId,
        ...addressData,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to update address");
    }
    return data.data;
  } catch (error) {
    console.error("Error updating address:", error);
    throw error;
  }
};

const deleteAddress = async (policyId, addressId) => {
  try {
    const response = await fetch(`${baseUrl}/deletePolicyAddress`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      body: JSON.stringify({ policy_id: policyId, id: addressId }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to delete address");
    }
    return data.data;
  } catch (error) {
    console.error("Error deleting address:", error);
    throw error;
  }
};

const checkDuplicate = async (policyId, addressData) => {
  try {
    const response = await fetch(`${baseUrl}/checkDuplicateAddress`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      body: JSON.stringify({ policy_id: policyId, ...addressData }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to check duplicate");
    }
    return data.data;
  } catch (error) {
    console.error("Error checking duplicate:", error);
    throw error;
  }
};

const importPoliciesFromClaims = async () => {
  try {
    const response = await fetch(`${baseUrl}/importPoliciesFromClaims`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      body: JSON.stringify({}),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to import policies");
    }
    return data.data;
  } catch (error) {
    console.error("Error importing policies:", error);
    throw error;
  }
};

const syncClaimFormPolicies = async () => {
  try {
    const response = await fetch(`${baseUrl}/syncClaimFormPolicies`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      body: JSON.stringify({}),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to sync claim form policies");
    }
    return data.data;
  } catch (error) {
    console.error("Error syncing claim form policies:", error);
    throw error;
  }
};

export const policiesService = {
  fetchPolicies,
  fetchPolicyDetails,
  createPolicy,
  updatePolicy,
  deletePolicy,
  fetchAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  checkDuplicate,
  importPoliciesFromClaims,
  syncClaimFormPolicies,
};
